/**
 * PATH: packages/email-hub/src/feature/components/rich-text/quoteBlockNode.tsx
 * FILE_ID: EMAILHUB-RICHTEXT-QUOTEBLOCKNODE-001
 * PURPOSE: TipTap atom node that embeds a quoted ORIGINAL email (when it
 *          contains layout tables) as an OPAQUE block: the editor never
 *          descends into it, so Outlook spacer tables can't be normalised
 *          into the empty-cell grid; the NodeView renders the sanitised
 *          original so the operator sees the real quoted message.
 * WHY:     BUG-2026-07-07-EMAIL-QUOTE-TABLE-GRID (see quoteBlock.ts).
 * FLOW:
 *   <div data-qte-quote="enc"> ─parseHTML▶ node{html} ─NodeView▶ preview
 *   getHTML ─renderHTML▶ <div data-qte-quote="enc"> (drafts round-trip)
 *   send: emailHtml.toEmailHtml expands to the raw original HTML.
 * RELATED:
 *   - signatureBlockNode.tsx: the atom-node pattern this mirrors
 */

import { Node, mergeAttributes } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import type { NodeViewProps } from '@tiptap/react'
import { X } from 'lucide-react'
import { decodeSignatureBlock, encodeSignatureBlock } from './signatureBlock'
import { QUOTE_BLOCK_ATTR } from './quoteBlock'
import { sanitizeEmailHtml } from './emailHtml'

function QuoteBlockView({ node, deleteNode, editor }: NodeViewProps) {
  const raw = (node.attrs.html as string) || ''
  // Defence-in-depth: sanitised again before innerHTML so a hand-edited
  // draft can't smuggle script in.
  const safe = sanitizeEmailHtml(raw)
  return (
    <NodeViewWrapper
      data-qte-quote-view=""
      className="group relative my-2"
    >
      {editor?.isEditable && (
        <button
          type="button"
          contentEditable={false}
          onClick={() => deleteNode()}
          className="absolute -top-2 right-2 hidden rounded bg-background p-0.5 text-muted-foreground shadow-sm hover:text-destructive group-hover:block"
          aria-label="Remove quoted message"
          title="Remove quoted message"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {/* The original is opaque to ProseMirror - render it verbatim. */}
      <div
        className="qte-quote-preview"
        dangerouslySetInnerHTML={{ __html: safe }}
      />
    </NodeViewWrapper>
  )
}

export const QuoteBlockNode = Node.create({
  name: 'quoteBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      html: {
        default: '',
        parseHTML: (el) => decodeSignatureBlock(el.getAttribute(QUOTE_BLOCK_ATTR)),
        renderHTML: (attrs) => ({
          [QUOTE_BLOCK_ATTR]: encodeSignatureBlock((attrs.html as string) || ''),
        }),
      },
    }
  },

  parseHTML() {
    return [{ tag: `div[${QUOTE_BLOCK_ATTR}]` }]
  },

  renderHTML({ HTMLAttributes }) {
    // Canonical serialisation kept EMPTY (the html lives in the attribute)
    // so the editor never re-parses the layout tables. toEmailHtml expands
    // it to the raw original on send.
    return ['div', mergeAttributes(HTMLAttributes)]
  },

  addNodeView() {
    return ReactNodeViewRenderer(QuoteBlockView)
  },
})
